// components/ProductOptionsDialog.jsx - Updated with promotion status check
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Chip,
  IconButton,
  Paper,
  Alert,
  Tooltip,
  Fade,
  LinearProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import InfoIcon from '@mui/icons-material/Info';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import { formatPrice } from '../components/CarrancyTrans';

const ProductOptionsDialog = ({ 
  open, 
  onClose, 
  product, 
  onAddToCart,
  language,
  keywordsData,
  currency
}) => {
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [basePrice, setBasePrice] = useState(0);
  const [qualifyingAmount, setQualifyingAmount] = useState(0);
  const [promotionQualified, setPromotionQualified] = useState(false);
  const [promotionReward, setPromotionReward] = useState(null);
  const [showReward, setShowReward] = useState(false);
  
  // Check if promotion is active from product data
  const isPromotionActive = product?.promotion_is_active === true;
  const promotionRemainingUses = product?.promotion_remaining_uses || 0;
  const isPromotionExpired = product?.has_promotion && !isPromotionActive;
  const isPromotionLimitReached = product?.has_promotion && 
                                  product?.promotion_max_uses && 
                                  promotionRemainingUses === 0;

  useEffect(() => {
    if (product) {
      // Set base price from product (with discount if applicable)
      const base = product?.final_price || product?.price || 0;
      setBasePrice(base);
      
      // Reset selections when product changes
      setSelectedSize(null);
      setSelectedExtras([]);
      setPromotionQualified(false);
      setPromotionReward(null);
      setShowReward(false);
    }
  }, [product]);

  useEffect(() => {
    calculateTotalPrice();
  }, [selectedSize, selectedExtras, basePrice]);

  useEffect(() => {
    // Check if promotion is active and qualifies
    if (product?.has_promotion && product?.promotion_threshold > 0 && isPromotionActive) {
      const extraTotal = (selectedSize?.price || 0) + 
        selectedExtras.reduce((sum, extra) => sum + (extra.price || 0), 0);
      
      setQualifyingAmount(extraTotal);
      
      if (extraTotal >= (product.promotion_threshold || 0)) {
        setPromotionQualified(true);
        
        // Set reward based on promotion type
        if (product.promotion_reward_type === 'product') {
          setPromotionReward({
            type: 'product',
            name: product.promotion_reward_name || 'Free Product',
            name_en: product.promotion_reward_name,
            value: product.promotion_reward_value || 0,
            id: product.promotion_reward_id
          });
        } else if (product.promotion_reward_type === 'extra') {
          setPromotionReward({
            type: 'extra',
            name: product.promotion_reward_name || 'Free Extra',
            value: 0,
            id: product.promotion_reward_id
          });
        } else if (product.promotion_reward_type === 'discount_percent') {
          setPromotionReward({
            type: 'discount',
            percent: product.promotion_reward_value,
            value: (totalPrice * (product.promotion_reward_value || 0) / 100).toFixed(2)
          });
        } else if (product.promotion_reward_type === 'discount_amount') {
          setPromotionReward({
            type: 'discount',
            amount: product.promotion_reward_value,
            value: product.promotion_reward_value
          });
        }
        
        // Show reward animation
        setShowReward(true);
        setTimeout(() => setShowReward(false), 3000);
      } else {
        setPromotionQualified(false);
        setPromotionReward(null);
      }
    } else {
      // Reset promotion state if not active
      setPromotionQualified(false);
      setPromotionReward(null);
    }
  }, [selectedSize, selectedExtras, totalPrice, product, isPromotionActive]);

  const calculateTotalPrice = () => {
    let total = basePrice;

    // Add size price if selected
    if (selectedSize) {
      total += selectedSize.price || 0;
    }

    // Add extras prices
    if (selectedExtras.length > 0) {
      total += selectedExtras.reduce((sum, extra) => sum + (extra.price || 0), 0);
    }

    setTotalPrice(total);
  };

  const handleSizeChange = (event) => {
    const sizeValue = event.target.value;
    const size = product?.sizes?.find(s => s.name === sizeValue || s.id === sizeValue);
    setSelectedSize(size);
  };

  const handleExtraToggle = (extra) => {
    setSelectedExtras(prev => {
      const exists = prev.find(e => e.name === extra.name || e.id === extra.id);
      if (exists) {
        return prev.filter(e => (e.name !== extra.name && e.id !== extra.id));
      } else {
        return [...prev, extra];
      }
    });
  };

  const handleAddToCart = () => {
    // Validate size selection if product has sizes
    if (product?.has_sizes && !selectedSize) {
      alert(language === 'ar' ? 'الرجاء اختيار المقاس' : 'Please select a size');
      return;
    }

    // Create the cart item object
    const cartItem = {
      product_id: product?.id,
      quantity: 1,
      selected_size: selectedSize,
      selected_extras: selectedExtras,
      base_price: basePrice,
      final_unit_price: totalPrice
    };

    // Add promotion data if qualified and promotion is active
    if (promotionQualified && product?.has_promotion && isPromotionActive) {
      cartItem.applied_promotion_id = product.id;
      cartItem.applied_promotion_data = {
        type: product.promotion_type,
        threshold: product.promotion_threshold,
        reward_type: product.promotion_reward_type,
        reward_name: product.promotion_reward_name,
        reward_id: product.promotion_reward_id,
        reward_value: product.promotion_reward_value,
        qualifying_amount: qualifyingAmount
      };
      
      if (promotionReward) {
        cartItem.free_reward_item_id = promotionReward.id;
        cartItem.free_reward_item_type = promotionReward.type;
      }
      
      cartItem.promotion_qualifying_amount = qualifyingAmount;
    }

    console.log('ProductOptionsDialog sending:', cartItem);
    onAddToCart(cartItem);
    onClose();
  };

  const isExtraSelected = (extra) => {
    return selectedExtras.some(e => e.name === extra.name || e.id === extra.id);
  };

  const getRemainingForPromotion = () => {
    if (!product?.has_promotion || !isPromotionActive) return 0;
    const needed = product.promotion_threshold || 0;
    const current = qualifyingAmount;
    return Math.max(0, needed - current);
  };

  const getProgressPercentage = () => {
    if (!product?.has_promotion || !product.promotion_threshold || !isPromotionActive) return 0;
    return Math.min((qualifyingAmount / product.promotion_threshold) * 100, 100);
  };

  // Decode Unicode escape sequences in promotion_icon
  const getPromotionIcon = () => {
    if (!product?.promotion_icon) return '🎁';
    try {
      return eval(`"${product.promotion_icon}"`);
    } catch (e) {
      return '🎁';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          backgroundColor: '#0D0D0D',
          border: '1px solid #FFD700',
          borderRadius: '20px',
          boxShadow: '0 12px 32px rgba(255, 215, 0, 0.2)',
          position: 'relative',
          overflow: 'visible'
        }
      }}
    >
      {/* Reward Animation - Only show if promotion is active */}
      {isPromotionActive && (
        <Fade in={showReward}>
          <Box
            sx={{
              position: 'absolute',
              top: -20,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: '#FFD700',
              color: '#000000',
              padding: '10px 20px',
              borderRadius: '50px',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: '0 4px 20px rgba(255, 215, 0, 0.5)',
              animation: 'bounce 1s ease'
            }}
          >
            <EmojiEventsIcon />
            <Typography sx={{ fontWeight: 800 }}>
              {language === 'ar' ? 'تهانينا! لقد حصلت على' : 'Congratulations! You got'}{' '}
              {promotionReward?.name || product?.promotion_reward_name || 'هدية مجانية'}
            </Typography>
          </Box>
        </Fade>
      )}

      <DialogTitle sx={{ 
        color: '#FFD700', 
        fontWeight: 800,
        borderBottom: '1px solid #2D2D2D',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 700 }}>
            {language === 'ar' ? 'تخصيص المنتج' : 'Customize Product'}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: '#B3B3B3' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Product Info */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#1A1A1A', borderRadius: '12px' }}>
          <Typography variant="h6" sx={{ color: '#FFD700', mb: 1 }}>
            {language === 'ar' ? product?.name : product?.name_en}
          </Typography>
          <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
            {language === 'ar' ? product?.description : product?.description_en}
          </Typography>
          
          {/* Promotion Badge - Show different states */}
          {product?.has_promotion && (
            <>
              {isPromotionActive ? (
                <Tooltip title={language === 'ar' ? product?.promotion_description : product?.promotion_description_en}>
                  <Chip
                    icon={<CardGiftcardIcon />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>{language === 'ar' ? product?.promotion_description : product?.promotion_description_en}</span>
                      </Box>
                    }
                    sx={{
                      mt: 1,
                      backgroundColor: 'rgba(255, 215, 0, 0.15)',
                      color: '#FFD700',
                      border: '1px solid #FFD700',
                      fontWeight: 600,
                      '& .MuiChip-icon': {
                        color: '#FFD700'
                      }
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip title={language === 'ar' ? 'العرض غير متاح' : 'Promotion not available'}>
                  <Chip
                    icon={isPromotionLimitReached ? <TimerOffIcon /> : <EventBusyIcon />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {isPromotionLimitReached 
                          ? (language === 'ar' ? 'انتهت الكمية المحدودة' : 'Limited quantity ended')
                          : (language === 'ar' ? 'انتهت صلاحية العرض' : 'Promotion expired')}
                      </Box>
                    }
                    sx={{
                      mt: 1,
                      backgroundColor: 'rgba(255, 61, 0, 0.1)',
                      color: '#FF3D00',
                      border: '1px solid #FF3D00',
                      fontWeight: 600,
                      '& .MuiChip-icon': {
                        color: '#FF3D00'
                      }
                    }}
                  />
                </Tooltip>
              )}
              
              {/* Show remaining uses if promotion is active */}
              {isPromotionActive && product?.promotion_max_uses && (
                <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', mt: 1 }}>
                  {language === 'ar' 
                    ? `متبقي ${promotionRemainingUses} من أصل ${product.promotion_max_uses} استخدام`
                    : `${promotionRemainingUses} of ${product.promotion_max_uses} uses remaining`}
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Base Price */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#1A1A1A', borderRadius: '12px' }}>
          <Typography variant="body1" sx={{ color: '#B3B3B3' }}>
            {language === 'ar' ? 'السعر الأساسي:' : 'Base Price:'}
          </Typography>
          <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 700 }}>
            {formatPrice(basePrice.toFixed(2), currency, language)}
          </Typography>
        </Box>

        {/* Promotion Progress - Only show if active */}
        {product?.has_promotion && product?.promotion_threshold > 0 && isPromotionActive && (
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#1A1A1A', borderRadius: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocalOfferIcon sx={{ color: '#FFD700' }} />
              <Typography sx={{ color: '#FFD700', fontWeight: 600 }}>
                {language === 'ar' ? 'عرض خاص' : 'Special Offer'}
              </Typography>
            </Box>
            
            <Typography variant="body2" sx={{ color: '#B3B3B3', mb: 1 }}>
              {language === 'ar' ? product?.promotion_description : product?.promotion_description_en}
            </Typography>
            
            {!promotionQualified ? (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                    {language === 'ar' ? 'المتبقي للحصول على الهدية:' : 'Remaining for gift:'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#FFD700', fontWeight: 700 }}>
                    {formatPrice(getRemainingForPromotion().toFixed(2), currency, language)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={getProgressPercentage()}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#2D2D2D',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#FFD700',
                      borderRadius: 3
                    }
                  }}
                />
              </Box>
            ) : (
              <Alert
                icon={<EmojiEventsIcon />}
                severity="success"
                sx={{
                  mt: 1,
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  color: '#4CAF50',
                  border: '1px solid #4CAF50'
                }}
              >
                {language === 'ar' 
                  ? `تهانينا! لقد حصلت على ${product?.promotion_reward_name || 'هدية مجانية'} مجاناً!`
                  : `Congratulations! You get a free ${product?.promotion_reward_name || 'gift'}!`}
              </Alert>
            )}
          </Box>
        )}

        {/* Expired Promotion Message */}
        {product?.has_promotion && !isPromotionActive && (
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#1A1A1A', borderRadius: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {isPromotionLimitReached ? (
                <TimerOffIcon sx={{ color: '#FF3D00' }} />
              ) : (
                <EventBusyIcon sx={{ color: '#FF3D00' }} />
              )}
              <Typography sx={{ color: '#FF3D00', fontWeight: 600 }}>
                {language === 'ar' ? 'العرض غير متاح' : 'Promotion Unavailable'}
              </Typography>
            </Box>
            
            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
              {isPromotionLimitReached 
                ? (language === 'ar' 
                    ? 'عذراً، انتهت الكمية المحدودة لهذا العرض'
                    : 'Sorry, the limited quantity for this offer has ended')
                : (language === 'ar' 
                    ? 'عذراً، انتهت صلاحية هذا العرض'
                    : 'Sorry, this promotion has expired')}
            </Typography>
          </Box>
        )}

        {/* Size Selection */}
        {product?.has_sizes && product?.sizes && product?.sizes.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <FormControl component="fieldset">
              <FormLabel sx={{ color: '#FFD700', fontWeight: 600, mb: 1 }}>
                {language === 'ar' ? 'اختر المقاس' : 'Select Size'}
              </FormLabel>
              <RadioGroup value={selectedSize?.name || ''} onChange={handleSizeChange}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {product.sizes.map((size, index) => (
                    <Paper
                      key={index}
                      sx={{
                        backgroundColor: '#1A1A1A',
                        border: selectedSize?.name === size.name ? '2px solid #FFD700' : '1px solid #2D2D2D',
                        borderRadius: '12px',
                        p: 1.5,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#FFD700',
                        }
                      }}
                    >
                      <FormControlLabel
                        value={size.name}
                        control={
                          <Radio 
                            sx={{
                              color: '#B3B3B3',
                              '&.Mui-checked': {
                                color: '#FFD700',
                              },
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', ml: 1 }}>
                            <Typography sx={{ color: '#FFFFFF' }}>
                              {language === 'ar' ? size.name_ar || size.name : size.name_en || size.name}
                            </Typography>
                            <Typography sx={{ color: '#FFD700', fontWeight: 600 }}>
                              +{formatPrice(size.price.toFixed(2), currency, language)}
                            </Typography>
                          </Box>
                        }
                        sx={{ width: '100%', m: 0 }}
                      />
                    </Paper>
                  ))}
                </Box>
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {/* Extras Selection */}
        {product?.has_extras && product?.extras && product?.extras.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <FormLabel sx={{ color: '#FFD700', fontWeight: 600, mb: 1 }}>
              {language === 'ar' ? 'إضافات إضافية' : 'Extras'}
            </FormLabel>
            <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', mb: 2 }}>
              {language === 'ar' ? 'يمكنك اختيار أكثر من إضافة' : 'You can select multiple extras'}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {product?.extras.map((extra, index) => (
                <Paper
                  key={index}
                  sx={{
                    backgroundColor: '#1A1A1A',
                    border: isExtraSelected(extra) ? '2px solid #FFD700' : '1px solid #2D2D2D',
                    borderRadius: '12px',
                    p: 1.5,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#FFD700',
                    }
                  }}
                  onClick={() => handleExtraToggle(extra)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography sx={{ color: '#FFFFFF' }}>
                        {language === 'ar' ? extra.name_ar || extra.name : extra.name_en || extra.name}
                      </Typography>
                      {extra.description && (
                        <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                          {language === 'ar' ? extra.description_ar || extra.description : extra.description_en || extra.description}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ color: '#FFD700', fontWeight: 600 }}>
                        +{formatPrice(extra.price.toFixed(2), currency, language)}
                      </Typography>
                      <Chip
                        label={isExtraSelected(extra) ? (language === 'ar' ? 'تم الاختيار' : 'Selected') : (language === 'ar' ? 'إضافة' : 'Add')}
                        size="small"
                        sx={{
                          backgroundColor: isExtraSelected(extra) ? '#FFD700' : '#2D2D2D',
                          color: isExtraSelected(extra) ? '#000000' : '#B3B3B3',
                          fontWeight: 600,
                          minWidth: 70
                        }}
                      />
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {/* Price Summary */}
        <Box sx={{ 
          mt: 3, 
          p: 2, 
          backgroundColor: '#1A1A1A',
          border: promotionQualified ? '2px solid #4CAF50' : '1px solid #FFD700',
          borderRadius: '12px',
          transition: 'border-color 0.3s ease'
        }}>
          <Typography variant="h6" sx={{ color: promotionQualified ? '#4CAF50' : '#FFD700', mb: 1 }}>
            {language === 'ar' ? 'ملخص السعر' : 'Price Summary'}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ color: '#B3B3B3' }}>
              {language === 'ar' ? 'السعر الأساسي:' : 'Base Price:'}
            </Typography>
            <Typography sx={{ color: '#FFFFFF' }}>
              {formatPrice(basePrice.toFixed(2), currency, language)}
            </Typography>
          </Box>

          {selectedSize && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? 'المقاس:' : 'Size:'} {language === 'ar' ? selectedSize.name_ar || selectedSize.name : selectedSize.name_en || selectedSize.name}
              </Typography>
              <Typography sx={{ color: '#4CAF50' }}>
                +{formatPrice(selectedSize.price.toFixed(2), currency, language)}
              </Typography>
            </Box>
          )}

          {selectedExtras.map((extra, index) => (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? extra.name_ar || extra.name : extra.name_en || extra.name}:
              </Typography>
              <Typography sx={{ color: '#4CAF50' }}>
                +{formatPrice(extra.price.toFixed(2), currency, language)}
              </Typography>
            </Box>
          ))}

          {promotionQualified && promotionReward && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ color: '#4CAF50', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CardGiftcardIcon fontSize="small" />
                {language === 'ar' ? 'هدية مجانية:' : 'Free Gift:'}
              </Typography>
              <Typography sx={{ color: '#4CAF50', fontWeight: 700 }}>
                {promotionReward.name || product?.promotion_reward_name || 'Free Item'}
              </Typography>
            </Box>
          )}

          <Divider sx={{ backgroundColor: '#2D2D2D', my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: promotionQualified ? '#4CAF50' : '#FFD700' }}>
              {language === 'ar' ? 'الإجمالي:' : 'Total:'}
            </Typography>
            <Typography variant="h5" sx={{ color: promotionQualified ? '#4CAF50' : '#FFD700', fontWeight: 900 }}>
              {formatPrice(totalPrice.toFixed(2), currency, language)}
            </Typography>
          </Box>
          
          {promotionQualified && (
            <Typography variant="caption" sx={{ color: '#4CAF50', display: 'block', textAlign: 'center', mt: 1 }}>
              ✨ {language === 'ar' ? 'شامل الهدية المجانية' : 'Includes free gift!'}
            </Typography>
          )}
        </Box>

        {/* Info Alert */}
        {(product?.has_sizes || product?.has_extras || product?.has_promotion) && (
          <Alert 
            icon={<InfoIcon />}
            severity="info"
            sx={{ 
              mt: 2,
              backgroundColor: 'rgba(255, 215, 0, 0.1)',
              color: '#FFFFFF',
              border: '1px solid #FFD700',
              '& .MuiAlert-icon': {
                color: '#FFD700',
              }
            }}
          >
            {language === 'ar' 
              ? 'يمكنك تخصيص طلبك حسب رغبتك. الأسعار النهائية تشمل جميع الإضافات المختارة.'
              : 'You can customize your order. Final price includes all selected options.'}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        borderTop: '1px solid #2D2D2D',
        justifyContent: 'space-between'
      }}>
        <Button 
          onClick={onClose}
          sx={{
            color: '#B3B3B3',
            '&:hover': { color: '#FFD700' }
          }}
        >
          {language === 'ar' ? 'إلغاء' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={handleAddToCart}
          startIcon={<AddShoppingCartIcon />}
          sx={{
            backgroundColor: promotionQualified ? '#4CAF50' : '#FFD700',
            color: promotionQualified ? '#FFFFFF' : '#000000',
            fontWeight: 800,
            px: 4,
            py: 1.5,
            '&:hover': {
              backgroundColor: promotionQualified ? '#45a049' : '#FFE55C',
            },
            animation: promotionQualified ? 'pulse 2s infinite' : 'none',
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.05)' },
              '100%': { transform: 'scale(1)' }
            }
          }}
        >
          {promotionQualified 
            ? (language === 'ar' ? 'أضف مع الهدية المجانية' : 'Add with Free Gift')
            : (language === 'ar' ? 'أضف إلى السلة' : 'Add to Cart')
          } - {formatPrice(totalPrice.toFixed(2), currency, language)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductOptionsDialog;